<template>
  <div :class="'image-gallery' + (dragOver ? ' image-gallery-dragover' : '')" @dragover="onDragOver" @dragleave="dragOver = false" @drop="onDrop">
    <ol class="sortable additional-images" v-if="images && images.length">
      <draggable group="additionalImages" :model-value="images" @update:model-value="$emit('update:images', $event)" class="additional-images-grid" draggable="li" :sort="true" :delay="100" :delayOnTouchOnly="true">
        <li v-for="image in images" :key="image.id">
          <span class="thumbnail">
            <img class="img-responsive" :src="previewUrl(image)" />
            <slot name="badge" :image="image"></slot>
            <button class="removeimage btn btn-danger" type="button" v-on:click="$emit('remove', image)"><i class="fa fa-fw fa-trash"></i></button>
          </span>
          <button type="button" class="btn btn-sm btn-default captionButton" v-if="showDetails" v-on:click="$emit('details', image)"><i class="fa fa-fw fa-edit"></i> {{ _('Details') }}</button>
        </li>
      </draggable>
    </ol>
    <p v-else-if="emptyText">{{ emptyText }}</p>
    <upload-progress :uploads="uploads" />
    <div class="row" v-if="!disabled && remaining > 0">
      <div class="col-xs-12">
        <label class="btn btn-success image-gallery-upload">
          <input type="file" accept="image/*" multiple :id="inputId" @change="onSelect" />
          <i class="fa fa-fw fa-image"></i> {{ _('Send images') }}
        </label>
        <button type="button" class="btn btn-default image-gallery-library" v-on:click="$emit('library', remaining)"><i class="fa fa-fw fa-images"></i> {{ _('Choose from files') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'
import { johkuEmbedDisplaySrc } from '~/lib/tiptap/johkuEmbedImage'

// Additional images (sharedFile) of an entity: sortable thumbnails plus a
// multi-file upload. Selected or dropped files are emitted as `upload`; the
// editor queues them with lib/uploadaware.js.
export default {
  mixins: [errors],
  props: {
    images: { type: Array, default: () => [] },
    ownerKey: { type: String, default: null },
    uploadSlot: { type: String, default: 'gallery' },
    max: { type: Number, default: 20 },
    emptyText: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Templates and receipt components have no captions
    showDetails: { type: Boolean, default: true },
    inputId: { type: String, default: null }
  },
  emits: ['update:images', 'upload', 'remove', 'details', 'library'],
  data () {
    return {
      dragOver: false
    }
  },
  computed: {
    ...mapGetters(['baseDomain', 'shopId']),
    uploads () {
      if (!this.ownerKey) {
        return []
      }
      return this.$store.state.uploads.filter(u => u.owner === this.ownerKey && u.slot === this.uploadSlot && u.status !== 'done')
    },
    // Uploads still running will take their places in the gallery as well
    remaining () {
      const running = this.uploads.filter(u => u.status !== 'error' && u.status !== 'canceled').length
      return this.max - (this.images ? this.images.length : 0) - running
    }
  },
  methods: {
    // Image CDN, so previews don't load the backend
    previewUrl (image) {
      return johkuEmbedDisplaySrc(this.baseDomain + this.shopId, image.id, 400)
    },
    emitFiles (files) {
      const images = files.filter(f => f.type.startsWith('image/'))
      if (images.length > this.remaining) {
        this.showWarning(this._('You can add at most %d more images.').replace('%d', Math.max(0, this.remaining)))
      }
      const accepted = images.slice(0, Math.max(0, this.remaining))
      if (accepted.length) {
        this.$emit('upload', accepted)
      }
    },
    onSelect (e) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      this.emitFiles(files)
    },
    onDragOver (e) {
      // Only react to files dragged in from outside, not to sorting the thumbnails
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
        e.preventDefault()
        this.dragOver = !this.disabled
      }
    },
    onDrop (e) {
      if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) {
        return
      }
      e.preventDefault()
      this.dragOver = false
      if (!this.disabled) {
        this.emitFiles(Array.from(e.dataTransfer.files))
      }
    }
  }
}
</script>

<style scoped>
.image-gallery-upload input[type=file] {
  display: none;
}
.image-gallery-library {
  margin-left: 6px;
}
.image-gallery-dragover {
  outline: 2px dashed #5cb85c;
  outline-offset: 4px;
}
</style>
